import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Divider,
  Avatar,
  IconButton,
  useTheme,
  useMediaQuery,
  Badge
} from '@mui/material';
import {
  Home as HomeIcon,
  Category as CategoryIcon,
  ShoppingBasket as ProductsIcon,
  BookOnline as BookingsIcon,
  Person as ProfileIcon,
  Logout as LogoutIcon,
  ShoppingCart as CartIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  LocationOn as AddressIcon
} from '@mui/icons-material';

const drawerWidth = 280;

const menuItems = [
  { text: 'Home', icon: <HomeIcon />, path: '/' },
  { text: 'Categories', icon: <CategoryIcon />, path: '/categories' },
  { text: 'Products', icon: <ProductsIcon />, path: '/products' },
  { text: 'Cart', icon: <CartIcon />, path: '/cart' },
  { text: 'My Address', icon: <AddressIcon />, path: '/my-address' },
  { text: 'My Bookings', icon: <BookingsIcon />, path: '/my-bookings' },
  { text: 'My Profile', icon: <ProfileIcon />, path: '/my-profile' },
];

const SideMenu = ({ onLogout, mobileOpen, onMobileToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [userInfo, setUserInfo] = useState({
    Name: '',
    MobileNo: '',
    EmailId: ''
  });
  const [cartCount, setCartCount] = useState(0);

  useEffect(() => {
    const user = localStorage.getItem('user');
    if (user) {
      try {
        const userData = JSON.parse(user);
        setUserInfo({
          Name: userData.Name || userData.name || '',
          MobileNo: userData.MobileNo || userData.mobileNo || userData.UserName || '',
          EmailId: userData.EmailId || userData.emailId || userData.email || ''
        });
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }
  }, []);

  // Effect to monitor cart changes
  useEffect(() => {
    const updateCartCount = () => {
      const cart = localStorage.getItem('cart');
      if (cart) {
        try {
          const cartItems = JSON.parse(cart);
          const totalItems = cartItems.reduce((total, item) => total + (item.quantity || 1), 0);
          setCartCount(totalItems);
        } catch (error) {
          console.error('Error parsing cart data:', error);
          setCartCount(0);
        }
      } else {
        setCartCount(0);
      }
    };

    // Initial load
    updateCartCount();

    // Listen for storage changes (when cart is updated from other components)
    const handleStorageChange = (e) => {
      if (e.key === 'cart') {
        updateCartCount();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    // Also check periodically for changes (for same-tab updates)
    const interval = setInterval(updateCartCount, 1000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  const handleMenuClick = (path) => {
    if (isMobile) {
      onMobileToggle();
    }
    navigate(path);
  };

  const handleLogout = () => {
    onLogout();
    navigate('/login');
  };

  return (
    <>
      {/* Mobile Menu Button */}
      {isMobile && (
        <IconButton
          color="inherit"
          aria-label="open drawer"
          edge="start"
          onClick={onMobileToggle}
          sx={{
            position: 'fixed',
            top: 16,
            left: 16,
            zIndex: 1200,
            backgroundColor: '#2E7D32',
            color: 'white',
            '&:hover': {
              backgroundColor: '#1B5E20',
            },
          }}
        >
          <MenuIcon />
        </IconButton>
      )}

      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={isMobile ? mobileOpen : true}
        onClose={isMobile ? onMobileToggle : undefined}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            backgroundColor: '#2E7D32',
            color: 'white',
            overflowX: 'hidden',
          },
        }}
      >
        {/* Mobile Close Button */}
        {isMobile && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
            <IconButton
              onClick={onMobileToggle}
              sx={{ color: 'white' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        )}
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Box
          component="img"
          src="/logo2.png"
          alt="UVANDUS"
          sx={{
            height: 60,
            width: 'auto',
            mb: 1,
            maxWidth: '100%'
          }}
        />
        <Typography variant="body2" sx={{ opacity: 0.8 }}>
          Fresh Delivery Portal
        </Typography>
      </Box>
      
      <Divider sx={{ backgroundColor: 'rgba(255,255,255,0.2)' }} />
      
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Avatar 
          sx={{ 
            width: 50, 
            height: 50, 
            backgroundColor: '#4CAF50',
            fontSize: '1.2rem',
            fontWeight: 'bold'
          }}
        >
          {userInfo.Name ? userInfo.Name.charAt(0).toUpperCase() : 'U'}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
            {userInfo.Name || 'User'}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.8, mb: 0.5, fontSize: '0.75rem' }}>
            {userInfo.MobileNo || 'No Mobile'}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.8, fontSize: '0.75rem' }}>
            {userInfo.EmailId || 'No Email'}
          </Typography>
        </Box>
      </Box>
      
      <Divider sx={{ backgroundColor: 'rgba(255,255,255,0.2)' }} />
      
      <List sx={{ flexGrow: 1, pt: 2 }}>
        {menuItems.map((item) => (
          <ListItem
            key={item.text}
            button
            onClick={() => handleMenuClick(item.path)}
            sx={{
              mx: 1,
              mb: 1,
              borderRadius: 2,
              backgroundColor: location.pathname === item.path ? 'rgba(255,255,255,0.1)' : 'transparent',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.1)',
              },
            }}
          >
            <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
              {item.text === 'Cart' ? (
                <Badge 
                  badgeContent={cartCount} 
                  color="error"
                  sx={{
                    '& .MuiBadge-badge': {
                      backgroundColor: '#ff4444',
                      color: 'white',
                      fontSize: '0.7rem',
                      fontWeight: 'bold',
                      minWidth: '18px',
                      height: '18px'
                    }
                  }}
                >
                  {item.icon}
                </Badge>
              ) : (
                item.icon
              )}
            </ListItemIcon>
            <ListItemText 
              primary={item.text}
              sx={{
                '& .MuiListItemText-primary': {
                  fontWeight: location.pathname === item.path ? 600 : 400,
                }
              }}
            />
          </ListItem>
        ))}
      </List>
      
      <Divider sx={{ backgroundColor: 'rgba(255,255,255,0.2)' }} />
      
      <List>
        <ListItem
          button
          onClick={handleLogout}
          sx={{
            mx: 1,
            mb: 1,
            borderRadius: 2,
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.1)',
            },
          }}
        >
          <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText primary="Logout" />
        </ListItem>
      </List>
    </Drawer>
    </>
  );
};

export default SideMenu; 